// src/pages/GestionPlanes.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { MdPriceCheck } from 'react-icons/md';
import { FaRocket, FaCheckCircle, FaStar, FaTrophy, FaCrown } from 'react-icons/fa';
import PlatformBackButton from '../components/PlatformBackButton';
import './GestionPlanes.css';

const getPlanIcon = (name = '') => {
    const n = name.toLowerCase();
    if (n.includes('demo')) return <FaRocket />;
    if (n.includes('basica') || n.includes('básica')) return <FaCheckCircle />;
    if (n.includes('estandar') || n.includes('estándar')) return <FaStar />;
    if (n.includes('premium plus')) return <FaCrown />;
    if (n.includes('premium')) return <FaTrophy />;
    return <FaStar />;
};

const getPlanAccentColor = (name = '') => {
    const n = name.toLowerCase();
    if (n.includes('demo')) return 'gray';
    if (n.includes('basica') || n.includes('básica')) return 'green';
    if (n.includes('estandar') || n.includes('estándar')) return 'blue';
    if (n.includes('premium plus')) return 'purple';
    if (n.includes('premium')) return 'orange';
    return 'blue';
};

const GestionPlanes = () => {
    const [planes, setPlanes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPlanId, setSelectedPlanId] = useState('');
    const [discountPct, setDiscountPct] = useState(0);
    const [includeVAT, setIncludeVAT] = useState(false);

    useEffect(() => {
        const fetchPlanes = async () => {
            const querySnapshot = await getDocs(collection(db, 'planes_suscripcion'));
            const planesList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            // Orden personalizado: Demo → Básica → Estándar → Premium → Premium Plus
            const desiredOrder = ['demo', 'básica', 'basica', 'estándar', 'estandar', 'premium', 'premium plus'];
            const normalizeName = (nombre = '') => nombre.toString().trim().toLowerCase();
            const orderIndex = (nombre) => {
                const normalized = normalizeName(nombre);
                const idx = desiredOrder.indexOf(normalized);
                return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
            };

            planesList.sort((a, b) => {
                const orderDiff = orderIndex(a.nombre) - orderIndex(b.nombre);
                if (orderDiff !== 0) {
                    return orderDiff;
                }
                // Fallback por si dos planes no están en la lista: ordena por lotes mínimos
                return (a.lotesMin || 0) - (b.lotesMin || 0);
            });
            setPlanes(planesList);
            setLoading(false);

            if (planesList.length > 0) {
                setSelectedPlanId(planesList[0].id);
            }
        };
        fetchPlanes();
    }, []);

    const handleCostoChange = (id, nuevoCosto) => {
        const updatedPlanes = planes.map(plan =>
            plan.id === id ? { ...plan, costoMensual: parseFloat(nuevoCosto) || 0 } : plan
        );
        setPlanes(updatedPlanes);
    };

    const handleSaveChanges = async () => {
        setLoading(true);
        try {
            const batch = [];
            planes.forEach(plan => {
                const planRef = doc(db, 'planes_suscripcion', plan.id);
                batch.push(updateDoc(planRef, { costoMensual: plan.costoMensual }));
            });
            await Promise.all(batch);
            alert('¡Planes actualizados con éxito!');
        } catch (error) {
            console.error("Error al guardar los planes:", error);
            alert('Hubo un error al guardar los cambios.');
        } finally {
            setLoading(false);
        }
    };

    const selectedPlan = planes.find(plan => plan.id === selectedPlanId);
    const monthlyCost = selectedPlan?.costoMensual ? Number(selectedPlan.costoMensual) : 0;
    const annualCostWithoutDiscount = monthlyCost * 12;
    const normalizedDiscount = Math.min(Math.max(Number(discountPct) || 0, 0), 100);
    const discountAmount = annualCostWithoutDiscount * (normalizedDiscount / 100);
    const annualCostWithDiscount = annualCostWithoutDiscount - discountAmount;
    const vatAmount = includeVAT ? annualCostWithDiscount * 0.16 : 0;
    const annualCostWithVAT = annualCostWithDiscount + vatAmount;

    if (loading && planes.length === 0) return <p>Cargando planes...</p>;

    return (
        <div className="gestion-planes-container">
            <div className="gestion-planes-header">
                <PlatformBackButton className="gestion-planes-back" />
                <div className="gestion-planes-title">
                    <h2>Configuración de Planes y Precios</h2>
                    <p>Actualiza los montos mensuales disponibles para cada tramo de lotes.</p>
                </div>
                <div className="header-icon"><MdPriceCheck /></div>
            </div>
            <div className="planes-grid">
                {planes.map(plan => (
                    <div key={plan.id} className={`plan-card-redesign border-${getPlanAccentColor(plan.nombre)}`}>
                        <div className="plan-card-header">
                            <div className={`plan-icon-box bg-${getPlanAccentColor(plan.nombre)}`}>
                                {getPlanIcon(plan.nombre)}
                            </div>
                            <div className="plan-name-info">
                                <h3>{plan.nombre}</h3>
                                <p>Lotes: <strong>{plan.lotesMin} - {plan.lotesMax}</strong></p>
                            </div>
                        </div>

                        <div className="plan-card-body">
                            <div className="price-input-group">
                                <label>Costo Mensual (MXN)</label>
                                <div className="input-with-currency">
                                    <span className="currency-symbol">$</span>
                                    <input
                                        type="number"
                                        value={plan.costoMensual}
                                        onChange={(e) => handleCostoChange(plan.id, e.target.value)}
                                        placeholder="0.00"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <section className="planes-calculator">
                <div className="calculator-card">
                    <div className="calculator-header">
                        <h3>Calculadora de Anualidad</h3>
                        <p>Selecciona un plan y aplica un descuento anual para estimar el total.</p>
                    </div>
                    <div className="calculator-controls">
                        <div className="calculator-field">
                            <label htmlFor="plan-select">Plan</label>
                            <select
                                id="plan-select"
                                value={selectedPlanId}
                                onChange={(event) => setSelectedPlanId(event.target.value)}
                            >
                                {planes.map((plan) => (
                                    <option key={plan.id} value={plan.id}>
                                        {plan.nombre}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="calculator-field">
                            <label htmlFor="discount-input">Descuento anual (%)</label>
                            <input
                                id="discount-input"
                                type="number"
                                min="0"
                                max="100"
                                step="0.5"
                                value={discountPct}
                                onChange={(event) => setDiscountPct(event.target.value)}
                                onBlur={() => setDiscountPct(normalizedDiscount)}
                            />
                        </div>
                        <div className="calculator-field iva-toggle">
                            <label htmlFor="iva-toggle">IVA (16%)</label>
                            <button
                                type="button"
                                id="iva-toggle"
                                className={includeVAT ? 'iva-switch active' : 'iva-switch'}
                                onClick={() => setIncludeVAT((prev) => !prev)}
                                aria-pressed={includeVAT}
                            >
                                <span className="iva-switch-thumb" />
                                <span className="iva-switch-label">
                                    {includeVAT ? 'Aplicando IVA' : 'Sin IVA'}
                                </span>
                            </button>
                        </div>
                    </div>
                    <div className="calculator-totals">
                        <div>
                            <span>Tarifa mensual actual</span>
                            <strong>${monthlyCost.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MXN</strong>
                        </div>
                        <div>
                            <span>Anualidad sin descuento</span>
                            <strong>${annualCostWithoutDiscount.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MXN</strong>
                        </div>
                        <div>
                            <span>Descuento aplicado</span>
                            <strong>-${discountAmount.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MXN</strong>
                        </div>
                        <div className="calculator-subtotal">
                            <span>Subtotal anual con descuento</span>
                            <strong>${annualCostWithDiscount.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MXN</strong>
                        </div>
                        <div className="calculator-vat">
                            <span>IVA 16%</span>
                            <strong>{includeVAT ? `+$${vatAmount.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MXN` : '$0.00 MXN'}</strong>
                        </div>
                        <div className="calculator-total">
                            <span>Total anual con IVA</span>
                            <strong>${annualCostWithVAT.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MXN</strong>
                        </div>
                    </div>
                </div>
            </section>
            <div className="save-actions">
                <button onClick={handleSaveChanges} disabled={loading}>
                    {loading ? 'Guardando...' : 'Guardar Cambios'}
                </button>
            </div>
        </div>
    );
};

export default GestionPlanes;